import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import moment from "moment";
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import { GetApp } from '@material-ui/icons';
import { CSVLink } from "react-csv";
import * as C from 'components';

const useStyles = makeStyles({
  loaderBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loaderBarImg: {
    width: 40,
  },
  tableContainer: {
    display: 'flex',
    minHeight: 200,
  },
  dateTableWrapper: {
    float: 'left',
  },
  dataTableWrapper: {
    float: 'left',
    overflow: 'scroll',
  },
  leftTable: {
    border: '1px solid #eee',
  },
  lblGameName: {
    color: '#000',
    fontSize: 13,
    minWidth: 200,
  },
  _GAIv: {
    height: 40,
    backgroundColor: "#f9f9f9",
    borderLeft: "1px solid #eee6e6",
    color: "#666",
    fontSize: 11,
    minWidth: 80,
    paddingRight: 10,
    textAlign: 'right',
  },
  dataHeaderTd: {
    width: 100,
    paddingRight: 10,
    borderBottom: '1px solid #eee6e6',
  },
  dataTableBody: {
    fontSize: 11,
    paddingRight: 10,
    paddingBottom: 0,
    textAlign: 'right',
    verticalAlign: 'middle',
    border: '1px solid #eee6e6',
    height: '3em',
    overflowX: 'auto',
  },
  dataCellTd: {
    fontSize: 13,
    paddingRight: 10,
    paddingBottom: 0,
    paddingLeft: 10,
    textAlign: 'right',
    verticalAlign: 'middle',
    borderBottom: '1px solid #eee6e6',
    borderRight: '1px solid #eee6e6',
    height: 45,
  },
  tTdHeader: {
    paddingLeft: '1rem',
    paddingRight: 19,
    fontWeight: 'bold',
    height: 39,
  },
  tTd: {
    height: 44,
    width: 250,
    paddingLeft: '1rem',
    borderBottom: '1px solid #eee',
    borderCollapse: 'collapse',
  },
  exportBtn: {
    marginLeft: 16,
  },
  titleContainer: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  widgetTitle: {
    flexBasis: '50%',
    fontSize: 16,
    padding: 16,
  },
  exportBtnContainer: {
    marginLeft: 'auto',
    padding: 16,
  }
});

// flowType -> 行ラベル
const FLOW_TYPE_LABEL_MAP = {
  OPERATOR_REVIEW_IF_AI_REVIEW_NG: "AIレビュー",
  OPERATOR_REVIEW_ONLY: "運用者レビュー",
};

// 表示順（小さいほど上）
const FLOW_TYPE_PRIORITY = {
  OPERATOR_REVIEW_IF_AI_REVIEW_NG: 1,
  OPERATOR_REVIEW_ONLY: 2,
};

const PicTreeTableReview = ({ reviewTrendBySeason, pageTopLoader, chartSize }) => {
  const classes = useStyles();

  if (
    !reviewTrendBySeason ||
    typeof reviewTrendBySeason !== "object" ||
    Object.keys(reviewTrendBySeason).length === 0
  ) {
    return null;
  }

  const flowTypes = Object.keys(reviewTrendBySeason).sort(
    (a, b) => (FLOW_TYPE_PRIORITY[a] || 99) - (FLOW_TYPE_PRIORITY[b] || 99)
  );
  if (flowTypes.length === 0) return null;

  // 先頭フローの bucketJst を基準に日付配列を作る
  const refFlowType = flowTypes[0];
  const refRows = reviewTrendBySeason[refFlowType] || [];
  const dates = refRows.map(r => r.bucketJst);

  // 行定義（AIレビュー完了 / 未完了 -> 運用者レビュー完了 / 未完了）
  const rowDefs = [];
  flowTypes.forEach(ft => {
    const baseLabel = FLOW_TYPE_LABEL_MAP[ft] || ft;
    const rows = reviewTrendBySeason[ft] || [];
    const rowMap = _.keyBy(rows, "bucketJst");

    rowDefs.push({
      key: ft + "_completed",
      label: baseLabel + "完了数",
      values: dates.map(d => Number(_.get(rowMap[d], "completedCount", 0))),
    });
    rowDefs.push({
      key: ft + "_uncompleted",
      label: baseLabel + "未完了数",
      values: dates.map(d => Number(_.get(rowMap[d], "uncompletedCount", 0))),
    });
  });

  const getLabelColumn = () => (
    <>
      {rowDefs.map(row => (
        <tr key={row.key}>
          <td className={classes.tTd}>
            <div className={classes.lblGameName}>{row.label}</div>
          </td>
        </tr>
      ))}
    </>
  );

  const getDataTable = () => (
    <>
      {rowDefs.map(row => (
        <tr key={row.key}>
          {row.values.map((val, i) => (
            <td key={i} className={classes.dataCellTd}>
              {val}
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  const getExportData = () => {
    const exportData = [];
    const header = [
      chartSize === "hour"
        ? "レビュー方法別 時間別レビュー件数"
        : "レビュー方法別 日別レビュー件数",
      ...dates.map(d => moment(d).format("YYYY-MM-DD HH:mm"))
    ];
    exportData.push(header);

    rowDefs.forEach(row => {
      exportData.push([row.label, ...row.values]);
    });

    return exportData;
  };

  const titleText =
    chartSize === "hour"
      ? "レビュー方法別 時間別レビュー件数"
      : "レビュー方法別 日別レビュー件数";

  return (
    <Paper>
      <div className={classes.titleContainer}>
        <div className={classes.widgetTitle}>{titleText}</div>
        <div className={classes.exportBtnContainer}>
          <CSVLink data={getExportData()} filename={`PicTree - レビュートレンド`}>
            <Button
              variant="contained"
              color="default"
              className={classes.exportBtn}
              startIcon={<GetApp />}
            >
              CSV出力
            </Button>
          </CSVLink>
        </div>
      </div>
      <div className={classes.tableContainer}>
        {pageTopLoader ? (
          <div className={classes.loaderBox}>
            <img
              className={classes.loaderBarImg}
              src={`/static/images/loader-bar.gif`}
            />
          </div>
        ) : (
          <>
            {/* 左側：行ラベル */}
            <div className={classes.dateTableWrapper}>
              <table className={classes.leftTable}>
                <tbody>
                  <tr>
                    <td className={classes.tTdHeader}></td>
                  </tr>
                  {getLabelColumn()}
                </tbody>
              </table>
            </div>

            {/* 右側：データ */}
            <div className={classes.dataTableWrapper}>
              <table className={classes.dataTableBody}>
                <tbody>
                  <tr className={classes._GAIv}>
                    {dates.map((date, i) => {
                      const m = moment(date);
                      return (
                        <td key={i} className={classes.dataHeaderTd}>
                          {chartSize === "hour" ? (
                            <>
                              {m.format("M/D")}
                              <br />
                              {m.format("HH:mm")}
                            </>
                          ) : (
                            m.format("M/D")
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {getDataTable()}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Paper>
  );
};

PicTreeTableReview.propTypes = {
  reviewTrendBySeason: PropTypes.object,
  pageTopLoader: PropTypes.bool,
  chartSize: PropTypes.string, // "day" | "hour"
};

const mapStateToProps = state => ({
  reviewTrendBySeason: state.pictree.reviewTrendBySeason,
  pageTopLoader: state.page.pageTopLoader,
});

export default connect(mapStateToProps)(PicTreeTableReview);
